import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plus, Loader2, Users2, Trash2, Pencil, Check, X, UserPlus, Search, ChevronLeft,
} from "lucide-react";
import { API } from "@/lib/api";


/* ── Add Members Dialog ── */
function AddMembersDialog({ open, onClose, vid, group, existingIds, onAdded }) {
  const { authHeaders } = useAuth();
  const [allCustomers, setAllCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected([]); setSearch("");
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/customers?client_id=${vid}&limit=100`, { headers: authHeaders() });
        setAllCustomers((data.customers || []).filter(c => !existingIds.includes(c.id)));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [open, vid, existingIds, authHeaders]);

  const filtered = allCustomers.filter(c => {
    const q = search.toLowerCase();
    return !q || (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q) || (c.mobile_number || "").includes(q);
  });

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const handleSave = async () => {
    if (selected.length === 0) return;
    setSaving(true);
    try {
      await axios.post(`${API}/customer-groups/${vid}/${group.id}/members`, { customer_ids: selected }, { headers: authHeaders() });
      onAdded();
      onClose(false);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-ink/5">
          <DialogTitle className="text-lg font-bold text-ink">Add customers to {group?.name}</DialogTitle>
        </DialogHeader>
        <div className="px-6 py-4 space-y-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/30" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customers" className="h-9 pl-9 rounded-xl text-sm" data-testid="member-search" />
          </div>
          <div className="max-h-72 overflow-y-auto space-y-1">
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 size={16} className="animate-spin text-ink/30" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-ink/40 text-center py-8">No customers available to add.</p>
            ) : filtered.map(c => {
              const on = selected.includes(c.id);
              return (
                <button key={c.id} type="button" onClick={() => toggle(c.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${on ? "bg-purple-100" : "hover:bg-surface"}`}
                  data-testid={`pick-customer-${c.id}`}>
                  <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-purple-500 border-ink" : "border-ink/30"}`}>
                    {on && <Check size={11} className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink truncate">{c.name || "Unknown"}</p>
                    <p className="text-[11px] text-ink/40 truncate">{c.email || c.mobile_number || "—"}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-ink/5 flex justify-end gap-3">
          <Button variant="ghost" onClick={() => onClose(false)} className="text-ink/60">Cancel</Button>
          <Button onClick={handleSave} disabled={saving || selected.length === 0} className="bg-purple-500 hover:bg-purple-600 text-white rounded-full gap-1.5" data-testid="confirm-add-members-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Add {selected.length > 0 ? `(${selected.length})` : ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Group Detail (members) ── */
function GroupDetail({ vid, group, onBack, onChanged }) {
  const { authHeaders } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/customer-groups/${vid}/${group.id}/members`, { headers: authHeaders() });
      setMembers(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [vid, group.id, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const handleRemove = async (cid) => {
    try {
      await axios.delete(`${API}/customer-groups/${vid}/${group.id}/members/${cid}`, { headers: authHeaders() });
      await load();
      onChanged();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-4" data-testid="group-detail">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-ink/60 hover:text-ink" data-testid="back-to-groups-btn">
          <ChevronLeft size={15} /> All groups
        </button>
        <Button size="sm" onClick={() => setAddOpen(true)} className="bg-purple-500 hover:bg-purple-600 text-white rounded-full gap-1.5 h-8 text-xs" data-testid="add-members-btn">
          <UserPlus size={13} /> Add Customers
        </Button>
      </div>
      <div>
        <h2 className="text-lg font-bold text-ink">{group.name}</h2>
        <p className="text-xs text-ink/40">{members.length} member{members.length !== 1 ? "s" : ""}</p>
      </div>

      <Card className="bg-white border-0 shadow-sm rounded-2xl">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 size={16} className="animate-spin text-ink/30" /></div>
          ) : members.length === 0 ? (
            <p className="text-sm text-ink/40 text-center py-12">No members yet. Add customers to this group.</p>
          ) : (
            <div className="divide-y divide-ink/5">
              {members.map(c => (
                <div key={c.id} className="flex items-center gap-3 px-4 py-3" data-testid={`member-row-${c.id}`}>
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-ink text-xs font-bold shrink-0">
                    {(c.name || "U")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink truncate">{c.name || "Unknown"}</p>
                    <p className="text-[11px] text-ink/40 truncate">{c.email || c.mobile_number || "—"}</p>
                  </div>
                  <span className="text-xs text-ink/50 mr-2">{(c.point_balance || 0).toLocaleString()} pts</span>
                  <button onClick={() => handleRemove(c.id)} className="p-1.5 rounded-lg text-ink/25 hover:text-danger hover:bg-danger/10" title="Remove" data-testid={`remove-member-${c.id}`}><X size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AddMembersDialog open={addOpen} onClose={setAddOpen} vid={vid} group={group} existingIds={members.map(m => m.id)} onAdded={() => { load(); onChanged(); }} />
    </div>
  );
}

/* ── MAIN ── */
export default function CustomerGroupsTab({ vendorId }) {
  const { authHeaders } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [activeGroup, setActiveGroup] = useState(null);

  const load = useCallback(async () => {
    if (!vendorId) return;
    try {
      const { data } = await axios.get(`${API}/customer-groups/${vendorId}`, { headers: authHeaders() });
      setGroups(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [vendorId, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/customer-groups/${vendorId}`, { name: newName.trim() }, { headers: authHeaders() });
      setNewName("");
      await load();
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  const handleRename = async (g) => {
    if (!editName.trim()) { setEditId(null); return; }
    try {
      await axios.put(`${API}/customer-groups/${vendorId}/${g.id}`, { name: editName.trim() }, { headers: authHeaders() });
      setEditId(null);
      await load();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? Members will not be deleted.`)) return;
    try {
      await axios.delete(`${API}/customer-groups/${vendorId}/${g.id}`, { headers: authHeaders() });
      await load();
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 size={18} className="animate-spin text-ink/30" /></div>;

  if (activeGroup) {
    return <GroupDetail vid={vendorId} group={activeGroup} onBack={() => { setActiveGroup(null); load(); }} onChanged={load} />;
  }

  return (
    <div className="space-y-4" data-testid="customer-groups-tab">
      <Card className="bg-white border-0 shadow-sm rounded-2xl">
        <CardContent className="px-5 py-4 flex items-center gap-2">
          <Input value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === "Enter" && handleCreate()} placeholder="New group name, e.g. VIPs, Lapsed customers" className="h-10 rounded-xl" data-testid="new-group-name" />
          <Button onClick={handleCreate} disabled={creating || !newName.trim()} className="bg-purple-500 hover:bg-purple-600 text-white rounded-full gap-1.5 shrink-0" data-testid="create-group-btn">
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create
          </Button>
        </CardContent>
      </Card>

      {groups.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-3"><Users2 size={20} className="text-ink" /></div>
          <p className="text-sm font-medium text-ink">No groups yet</p>
          <p className="text-xs text-ink/40 mt-1">Create a group to organise customers and send them targeted nudges.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="groups-grid">
          {groups.map(g => (
            <div key={g.id} className="bg-white rounded-2xl shadow-sm p-4 group" data-testid={`group-card-${g.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center shrink-0"><Users2 size={18} className="text-ink" /></div>
                <div className="flex-1 min-w-0">
                  {editId === g.id ? (
                    <div className="flex items-center gap-1">
                      <Input value={editName} onChange={e => setEditName(e.target.value)} onKeyDown={e => e.key === "Enter" && handleRename(g)} className="h-7 text-sm" autoFocus data-testid="rename-group-input" />
                      <button onClick={() => handleRename(g)} className="p-1 text-success"><Check size={14} /></button>
                      <button onClick={() => setEditId(null)} className="p-1 text-ink/40"><X size={14} /></button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-ink truncate">{g.name}</p>
                      <p className="text-[11px] text-ink/40">{g.member_count} member{g.member_count !== 1 ? "s" : ""}</p>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-ink/5">
                <button onClick={() => setActiveGroup(g)} className="text-xs font-medium text-ink/70 hover:text-ink" data-testid={`manage-group-${g.id}`}>Manage members →</button>
                <div className="flex items-center gap-1">
                  <button onClick={() => { setEditId(g.id); setEditName(g.name); }} className="p-1.5 rounded-lg text-ink/25 hover:text-ink hover:bg-purple-50" title="Rename" data-testid={`rename-group-${g.id}`}><Pencil size={13} /></button>
                  <button onClick={() => handleDelete(g)} className="p-1.5 rounded-lg text-ink/25 hover:text-danger hover:bg-danger/10" title="Delete" data-testid={`delete-group-${g.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
